// NodeEmailField.jsx

import React, { useCallback, useEffect, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { buildMailtoUrl, normalizeEmailValueForStorage } from 'utilities/Email';

// CSS
import './NodeEmailField.css';

// Icons
import Icon from 'svg/Icon';

// Components
import TextInput from 'components/textinput/TextInput';

/**
 * NodeEmailField
 *
 * Displays a dedicated email input with canonical save behavior and a compose action.
 * In readOnly / previewMode (visitor Peek): input is display-only; compose (mailto) stays.
 *
 * @param {string} value - Current stored field value.
 * @param {Function} onChange - Callback when a new email value is saved.
 * @param {boolean} [readOnly=false] - Display-only input.
 * @param {boolean} [previewMode=false] - Alias for readOnly (NodeField visitor path).
 *
 * @returns {ReactNode} NodeEmailField.
 */
const NodeEmailField = ({
  value,
  onChange,
  readOnly = false,
  previewMode = false,
}) => {

  // ----- CONTEXT ----- //

  const { theme } = useTheme();
  const isReadOnly = Boolean(readOnly || previewMode);

  // ----- STATE ----- //

  const [draftValue, setDraftValue] = useState(value == null ? '' : String(value));

  // ----- EFFECTS ----- //

  useEffect(() => {
    setDraftValue(value == null ? '' : String(value));
  }, [value]);

  // ----- HANDLERS ----- //

  const handleChange = useCallback((event) => {
    if (isReadOnly) return;
    setDraftValue(event?.target?.value ?? '');
  }, [isReadOnly]);

  const handleBlur = useCallback(() => {
    if (isReadOnly || typeof onChange !== 'function') return;

    const nextValue = normalizeEmailValueForStorage(draftValue);
    const currentValue = normalizeEmailValueForStorage(value);

    if (nextValue !== currentValue) {
      onChange(nextValue);
      return;
    }

    setDraftValue(currentValue);
  }, [draftValue, isReadOnly, onChange, value]);

  const handleCompose = useCallback(() => {
    if (typeof window === 'undefined') return;

    const mailtoUrl = buildMailtoUrl(draftValue);
    if (!mailtoUrl) return;

    window.location.assign(mailtoUrl);
  }, [draftValue]);

  // ----- RENDER ----- //

  return (
    <>
      <div className="node-email-field-container">

        <div className="node-email-row">

          <TextInput
            type="email"
            inputMode="email"
            autoComplete="email"
            value={draftValue}
            placeholder={isReadOnly ? '' : 'name@example.com'}
            startIconKey="mail"
            startIconColor={theme.foregroundMuted}
            iconSize={22}
            hybrid
            readOnly={isReadOnly}
            onBlur={handleBlur}
            onChange={isReadOnly ? undefined : handleChange}
          />

          <div
            className="node-email-compose"
            title="Compose email"
            role="button"
            tabIndex={0}
            onClick={handleCompose}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleCompose();
              }
            }}
          >
            <Icon
              iconKey="open_in_new"
              color={theme.foregroundMuted}
              size={18}
            />
          </div>

        </div>
      </div>
    </>
  );
};

NodeEmailField.displayName = 'NodeEmailField';

export default NodeEmailField;
